import React, { memo, useCallback, useEffect, useMemo, useState } from 'react';

import { songs } from '@/assets/icons';
import {
	ChatContainer,
	ChatContainerSendMessageFn,
} from '@/components/ChatContainer';
import {
	INITIAL_MESSAGES_SIZE,
	sendingMessageStatus,
} from '@/config/constants';
import { directMessagesUrl } from '@/config/urls';
import {
	FetchChatReactionsMutation,
	GetChatQuery,
	MessageType,
	Permissions,
	TeamActionActions,
	useFetchChatMessagesMutation,
	useFetchChatReactionsMutation,
	useFetchMessageReactionsMutation,
	useGetChatQuery,
	useGetChatUserStatsQuery,
	useSendChatMessageMutation,
	useSendChatTypingStatusMutation,
	useUpsertChatStatMutation,
} from '@/graphql';
import { classNames } from '@/helpers/classNames';
import { deduplicateMessages } from '@/helpers/deduplicateMessages';
import { useDocumentEvent } from '@/hooks/useDocumentEvent';
import { useTimeoutState } from '@/hooks/useTimeoutState';
import { useWindowEvent } from '@/hooks/useWindowEvent';
import { useAuth } from '@/providers/AuthProvider';
import { usePermissions } from '@/providers/PermissionsProvider';
import { useSubscription } from '@/providers/SubscriptionProvider';
import { useTeamActionSubscription } from '@/providers/TeamActionSubscription';
import { useTeam } from '@/providers/TeamProvider';
import { SvgIcon } from '@material-ui/core';
import { subDays } from 'date-fns';
import { useSnackbar } from 'notistack';
import { groupBy, last } from 'ramda';
import { useNavigate, useParams } from 'react-router';
import { v4 as uuidv4 } from 'uuid';

import styles from './PollMessagesAndRenderChatContainer.module.scss';

// const ChatContainerMemoized = memo(ChatContainer, (prevProps, nextProps) => {
// 	return (
// 		prevProps.chat?.messages?.items.length ===
// 			nextProps.chat?.messages?.items.length &&
// 		prevProps.previousMessagesLoading ===
// 			nextProps.previousMessagesLoading &&
// 		prevProps.hasMoreMessages === nextProps.hasMoreMessages
// 	);
// });

const byId = groupBy(function (
	reaction: FetchChatReactionsMutation['getChatReactions'][0],
) {
	return reaction.messageId;
});

type PollMessagesAndRenderChatContainerProps = {
	className?: string;
	setSongShareIconEl: React.Dispatch<
		React.SetStateAction<HTMLElement | null>
	>;
	handleSongShare: () => void;
	chat: GetChatQuery['getChat'];
};

export const PollMessagesAndRenderChatContainer: React.FC<
	PollMessagesAndRenderChatContainerProps
> = ({ className, setSongShareIconEl, handleSongShare, chat }) => {
	const { user } = useAuth();
	const { team } = useTeam();

	const isAdmin = useMemo(
		() =>
			team?.members
				.find((m) => m.user.id === user?.id)
				?.roles.includes('teamAdmin') ?? false,
		[team, user],
	);

	const { enqueueSnackbar } = useSnackbar();
	const { hasPermission } = usePermissions();
	const { actions } = useTeamActionSubscription();
	const [typingUserIds, setTypingUserIds] = useTimeoutState<string[]>([]);

	const [offset, setOffset] = useState(INITIAL_MESSAGES_SIZE);
	const [previousMessagesLoading, setPreviousMessagesLoading] =
		useState(false);
	const [hasMoreMessages, setHasMoreMessages] = useState(
		chat.messages?.totalCount !== chat.messages?.items.length,
	);

	useEffect(() => {
		setHasMoreMessages(
			chat.messages?.totalCount !== chat.messages?.items.length,
		);
	}, [chat.messages?.totalCount, chat.messages?.items.length]);

	const items = chat.messages?.items ?? [];
	const isGated =
		items.length > 0 &&
		new Date(items[0].createdAt) < subDays(new Date(), 30);

	// Track whether the user has accepted the gate this session.
	// Replaces isGated once accepted so hasMoreMessages suppression is lifted.
	const [gateAccepted, setGateAccepted] = useState(() =>
		Boolean(localStorage.getItem('gate_accepted_chat_history')),
	);
	useEffect(() => {
		const handler = (e: Event) => {
			const detail = (e as CustomEvent<{ gateName: string }>).detail;
			if (detail.gateName === 'chat_history') setGateAccepted(true);
		};
		window.addEventListener('gate:accepted', handler);
		return () => window.removeEventListener('gate:accepted', handler);
	}, []);

	const { status: subscriptionStatus } = useSubscription();
	const effectiveIsGated = isGated && !gateAccepted && !subscriptionStatus;

	const { mutateAsync: fetchChatMessages } = useFetchChatMessagesMutation({
		cacheTime: 0,
	});
	const { mutateAsync: fetchMessageReactions } =
		useFetchMessageReactionsMutation();
	const { mutateAsync: sendChatMessage } = useSendChatMessageMutation();
	const { mutateAsync: upsertChatStat } = useUpsertChatStatMutation();
	const { mutateAsync: sendChatTypingStatus } =
		useSendChatTypingStatusMutation();
	const { data: userStatsQuery, refetch: refetchUserStats } =
		useGetChatUserStatsQuery(
			{
				chatId: chat.id,
			},
			{
				refetchOnWindowFocus: false,
			},
		);

	// When the user opens the page we should record a seen stat immediatelly
	useEffect(() => {
		upsertChatStat({
			chatId: chat.id,
		});
	}, [upsertChatStat, chat.id]);

	const fetchNewMessageReactions = useCallback(
		async (messageId: string) => {
			try {
				const reactions = await fetchMessageReactions({
					messageId: messageId,
				});
				console.log(reactions.getMessageReactions);
				if (chat?.messages?.items) {
					chat.messages.items = chat.messages.items.map((m) => {
						if (m.id === messageId) {
							return {
								...m,
								reactions: reactions.getMessageReactions || [],
							};
						}
						return m;
					});
				}
			} catch (e) {
				console.log(e);
			}
		},
		[chat.messages, fetchMessageReactions],
	);

	const fetchNewMessages = useCallback(async () => {
		try {
			const allSentMessages =
				chat?.messages?.items?.filter(
					(m) => m.createdAt !== sendingMessageStatus,
				) || [];

			const lastDate =
				last(allSentMessages)?.createdAt || new Date().toISOString();

			const newMessages = await fetchChatMessages({
				chatId: chat.id,
				input: {
					limit: -1,
					offset: 0,
					afterDate: lastDate,
				},
			});

			upsertChatStat({
				chatId: chat.id,
			});

			if (chat?.messages?.items) {
				chat.messages.items = deduplicateMessages([
					...chat.messages.items,
					...(newMessages.getChat.messages?.items ?? []),
				]);
				chat.messages.totalCount =
					newMessages.getChat.messages?.totalCount ?? 0;
			}
		} catch (e) {
			console.log(e);
		}
	}, [chat.id, chat.messages, fetchChatMessages, upsertChatStat]);

	useWindowEvent('focus', () => fetchNewMessages());
	useDocumentEvent('visibilitychange', () => {
		if (document.visibilityState === 'visible') {
			fetchNewMessages();
		}
	});

	useEffect(() => {
		const lastAction = actions.splice(-1)[0];

		if (lastAction) {
			if (lastAction.resourceIds.includes(chat.id)) {
				// Handle TeamActionActions.ChatMessageSent
				if (lastAction.action === TeamActionActions.ChatMessageSent) {
					// When the user fetches new messages, they also update their
					// last seen at status
					fetchNewMessages();

					// Clear all typing users if a new message comes in
					// and it's not from the current user
					if (lastAction.actorId !== user.id) {
						setTypingUserIds([], {
							timeout: 10,
						});
					}
				}
				// Handle TeamActionActions.ChatLastSeenChanged
				if (
					lastAction.action === TeamActionActions.ChatLastSeenChanged
				) {
					refetchUserStats();
				}
				// Handle TeamActionActions.ChatTypingStatus
				if (lastAction.action === TeamActionActions.ChatTypingStatus) {
					const otherTypingUsers = typingUserIds.filter(
						(uid) => uid !== lastAction.actorId,
					);
					setTypingUserIds(
						[...otherTypingUsers, lastAction.actorId],
						{
							timeout: 5000,
						},
					);
				}
			}
			// Handle TeamActionActions.ChatReactionSent
			if (lastAction.action === TeamActionActions.ChatReactionSent) {
				const messageExistsInThisChat = chat.messages?.items.find((m) =>
					lastAction.resourceIds.includes(m.id),
				);
				if (messageExistsInThisChat) {
					fetchNewMessageReactions(messageExistsInThisChat.id);
				}
			}
		}

		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [actions, fetchNewMessages]);

	const sendMessage = useCallback<ChatContainerSendMessageFn>(
		async (value, type, metadata) => {
			const id = uuidv4();
			if (chat?.messages?.items) {
				chat.messages.items = [
					...chat.messages.items,
					{
						metadata,
						type: type ?? MessageType.Text,
						content: value,
						createdAt: sendingMessageStatus,
						id,
						reactions: [],
						userId: user.id,
						user: {
							displayName: user.displayName,
							id: user.id,
						},
					},
				];
			}
			// When we call the sendChatMessage mutation, a pub-sub event will be sent
			// which will notfiy all subscribers, including the current user. Eventhough he is the
			// actor, there is a "includeActor" field that includes the actor as a recipient.
			// This will trigger fetchNewMessages for us due to TeamActionActions.ChatMessageSent
			return sendChatMessage({
				chatId: chat.id,
				input: { id, content: value, type, metadata },
			});
		},
		[chat?.messages, sendChatMessage, chat.id, user.displayName, user.id],
	);

	const fetchPreviousMessages = useCallback(async () => {
		setPreviousMessagesLoading(true);
		const previousMessagesQuery = await fetchChatMessages({
			chatId: chat.id,
			input: {
				limit: INITIAL_MESSAGES_SIZE,
				offset,
			},
		});
		const previousMessages =
			previousMessagesQuery.getChat.messages?.items ?? [];
		if (chat?.messages?.items) {
			chat.messages.items = deduplicateMessages([
				...previousMessages,
				...chat.messages.items,
			]);
		}

		setOffset((prevOffset) => prevOffset + INITIAL_MESSAGES_SIZE);
		setPreviousMessagesLoading(false);
	}, [chat?.messages, fetchChatMessages, offset, chat.id]);

	const sendTypingStatus = (message) => {
		if (message) {
			console.log('Message input changed');
			sendChatTypingStatus({
				chatId: chat.id,
			});
		}
	};

	return (
		<ChatContainer
			className={classNames([
				styles.pollmessagesandrenderchatcontainer,
				className,
			])}
			fetchPreviousMessages={fetchPreviousMessages}
			previousMessagesLoading={previousMessagesLoading}
			chat={chat}
			userStats={
				userStatsQuery?.getChat.userStats
					? userStatsQuery?.getChat.userStats
					: chat.userStats
			}
			sendMessage={sendMessage}
			hasMoreMessages={effectiveIsGated ? false : hasMoreMessages}
			isGated={effectiveIsGated}
			isAdmin={isAdmin}
			tralingChatBarComponent={
				<div
					className={styles['songlist_icon']}
					onClick={(e) => {
						if (hasPermission(Permissions.SetlistCreate)) {
							return setSongShareIconEl(e.currentTarget);
						}
						handleSongShare();
					}}
				>
					<SvgIcon component={songs} />
				</div>
			}
			onMessageInputThrottled={sendTypingStatus}
			typingUserIds={typingUserIds}
			members={(chat.users ?? [])
				.filter(({ id }) => id !== user.id)
				.map(({ id, displayName }) => ({ id, displayName }))}
		/>
	);
};
